import React, { useEffect, useState } from 'react';
import { Globe, Flame, ChevronLeft, Loader2, Calendar, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { UIImage, AppView } from '../types.ts';

interface TopRotViewProps {
  setPreviewImage: (img: UIImage | null) => void;
  setActiveView: (view: AppView) => void;
}

type TimeFrame = 'daily' | 'weekly' | 'monthly' | 'all-time';

export default function TopRotView({ setPreviewImage, setActiveView }: TopRotViewProps) {
  const [memes, setMemes] = useState<UIImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<TimeFrame>('daily');

  useEffect(() => {
    async function fetchTopRot() {
      setIsLoading(true);
      try {
        let query;

        if (timeframe === 'all-time') {
          query = supabase
            .from('memes')
            .select('*')
            .eq('is_public', true)
            .order('likes_count', { ascending: false })
            .limit(30);
          
          const { data, error } = await query;
          if (error) throw error;
          setMemes(data || []);
        } else {
          const days = timeframe === 'daily' ? 1 : timeframe === 'weekly' ? 7 : 30;
          const dateLimit = new Date();
          dateLimit.setDate(dateLimit.getDate() - days);

          const { data: voteAgg, error: voteError } = await supabase
            .from('votes')
            .select('meme_id')
            .eq('vote_type', 'like')
            .gt('created_at', dateLimit.toISOString());

          if (voteError) throw voteError;

          const counts: Record<string, number> = {};
          voteAgg?.forEach(v => {
            counts[v.meme_id] = (counts[v.meme_id] || 0) + 1;
          });

          const sortedIds = Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 30)
            .map(([id]) => id);

          if (sortedIds.length === 0) {
            setMemes([]);
          } else {
            const { data: memeData, error: memeError } = await supabase
              .from('memes')
              .select('*')
              .in('id', sortedIds)
              .eq('is_public', true);

            if (memeError) throw memeError;

            const finalSorted = (memeData || []).sort((a, b) => 
              sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id)
            );
            
            const memesWithPeriodLikes = finalSorted.map(m => ({
              ...m,
              likes_count: counts[m.id]
            }));

            setMemes(memesWithPeriodLikes);
          }
        }
      } catch (e) {
        console.error("TOP ROT ERROR", e);
      } finally {
        setIsLoading(false);
      }
    }
    fetchTopRot();
  }, [timeframe]);

  const getRarityStyles = (rarity?: string) => {
    switch (rarity) {
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)]';
      case 'cursed': return 'border-red-600 shadow-[0_0_20px_rgba(220,38,38,0.6)]';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_30px_rgba(255,255,255,0.7)]';
      default: return 'border-zinc-800';
    }
  };

  const timeframeLabels: Record<TimeFrame, string> = {
    daily: "LAST 24H",
    weekly: "LAST 7 DAYS",
    monthly: "LAST 30 DAYS",
    'all-time': "ALL TIME LEGENDS"
  };

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-8">
      {/* Header Dashboard Card - Restored Original Sizing */}
      <div className="bg-zinc-950/60 backdrop-blur-xl border-2 border-white/5 rounded-[3rem] p-8 shadow-2xl space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
              <ChevronLeft size={24}/>
            </button>
            <div className="flex flex-col">
              <h2 className="text-5xl font-bungee text-yellow-400 uppercase tracking-tighter">TOP ROT</h2>
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Global Hall of Rizz</p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-1.5 bg-black/40 rounded-2xl border-2 border-zinc-800 shadow-xl overflow-x-auto whitespace-nowrap scrollbar-hide">
            {(['daily', 'weekly', 'monthly', 'all-time'] as TimeFrame[]).map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-5 py-2.5 rounded-xl font-bungee text-[10px] uppercase transition-all flex items-center gap-2 ${timeframe === tf ? 'bg-yellow-400 text-black shadow-lg scale-105' : 'text-zinc-500 hover:text-zinc-300'}`}
                aria-label={`Show ${tf.replace('-', ' ')} top rots`}
              >
                {tf === 'daily' ? <Zap size={14}/> : <Calendar size={14}/>}
                {tf.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 border-l-4 border-yellow-400 pl-4 py-1">
          <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">STATUS:</span>
          <span className="text-sm font-bungee text-white uppercase tracking-tight">{timeframeLabels[timeframe]}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 text-center flex flex-col items-center gap-6 opacity-40">
          <Loader2 size={48} className="animate-spin text-yellow-400" />
          <span className="font-bungee text-2xl uppercase tracking-widest text-zinc-400">Loading the Feed...</span>
        </div>
      ) : memes.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {memes.map((meme, idx) => (
            <div 
              key={meme.id} 
              onClick={() => setPreviewImage(meme)}
              className={`group relative aspect-square bg-zinc-900 rounded-[2.5rem] overflow-hidden border-4 shadow-xl cursor-pointer transition-all hover:scale-105 active:scale-95 ${getRarityStyles(meme.rarity)}`}
            >
              <img src={meme.url} className="w-full h-full object-cover" alt={meme.name} />
              <div className={`absolute top-4 right-4 -rotate-12 font-bungee text-xs px-3 py-1 rounded-full shadow-lg z-10 ${idx === 0 ? 'bg-yellow-400 text-black animate-pulse shadow-[0_0_20px_rgba(250,196,21,0.5)]' : idx === 1 ? 'bg-zinc-300 text-black' : idx === 2 ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-white'}`}>
                #{idx + 1}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent flex items-center justify-between">
                <span className="font-bungee text-[10px] uppercase text-white truncate mr-2 drop-shadow-md">{meme.name}</span>
                <div className="flex items-center gap-1 text-orange-500">
                  <Flame size={14} className="fill-orange-500 drop-shadow-[0_0_8px_rgba(249,115,22,0.6)]" />
                  <span className="font-bungee text-[10px]">{meme.likes_count || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-40 text-center font-bungee opacity-20 text-3xl uppercase tracking-widest border-4 border-dashed border-zinc-800 rounded-[3rem] bg-zinc-900/20 shadow-inner">
          No Activity Detected
        </div>
      )}
    </div>
  );
}
